import { useEffect, useState } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import axios from 'axios';

const getApiBaseUrl = () => {
  const envBase = process.env.REACT_APP_API_URL;
  if (envBase) {
    let base = envBase.endsWith('/') ? envBase.slice(0, -1) : envBase;
    if (base.endsWith('/api')) {
      base = base.slice(0, -4);
    }
    return base;
  }
  return 'http://localhost:3000';
};

// Card paths that need visibility checking
const CARD_PATHS = {
  '/user/job-alerts': 'job_alerts',
  '/user/career-profile': 'career_profile',
  '/user/resume': 'resume_generator',
  '/user/coupons': 'coupons',
  '/services': 'services',
  '/courses': 'courses',
};

// Cards that should always be accessible (bypass visibility check)
const ALWAYS_ACCESSIBLE_CARDS = [
  '/user/career-profile',
  '/user/resume',
  '/user/coupons',
  '/services',
  '/courses',
];

const CardVisibilityGuard = ({ children }) => {
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkCardVisibility();
  }, [location.pathname]);

  const checkCardVisibility = async () => {
    // Always allow access to essential citizen pages.
    if (ALWAYS_ACCESSIBLE_CARDS.includes(location.pathname)) {
      setIsVisible(true);
      setLoading(false);
      return;
    }
    
    // Check if current path is a card path
    const cardKey = CARD_PATHS[location.pathname];
    
    if (!cardKey) {
      // Not a card path, allow access
      setIsVisible(true);
      setLoading(false);
      return;
    }

    try {
      const baseUrl = getApiBaseUrl();
      const response = await axios.get(
        `${baseUrl}/api/dashboard-cards/check/${encodeURIComponent(location.pathname)}`
      );

      if (response.data.success) {
        setIsVisible(response.data.isVisible);
      } else {
        // On error, allow access (fail open for backward compatibility)
        setIsVisible(true);
      }
    } catch (error) {
      console.error('Error checking card visibility:', error);
      // On error, allow access (fail open for backward compatibility)
      setIsVisible(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Checking access...
        </Typography>
      </Box>
    );
  }

  if (isVisible === false) {
    return <Navigate to="/user/dashboard" replace />;
  }

  return children;
};

export default CardVisibilityGuard;

